import { Filter } from "./types";

import { Box } from "@mui/system";
import { TIME_RANGES } from "../creation/TimeRangeSelect";
import React, { useContext, useEffect, useState } from "react";
import CheckedDropDown from "../common/CheckedDropDown";
import { Family } from "../family/types";
import { CurrentUserContext } from "../context/RequireAuth";
import { isUserAdmin, ROLE_ADMIN } from "../../common/userUtils";
import useIsMobile from "../hooks/useIsMobile";
import { useListAllFamilies } from "../../services/admin";
import { useListAdvisorFamilies } from "../../services/advisor";
import CheckBoxFilter from "./CheckBoxFilter";

const useListFamilies = (adminMode: boolean = false) => {
  const currentUser = useContext(CurrentUserContext);
  return adminMode && isUserAdmin(currentUser)
    ? useListAllFamilies()
    : useListAdvisorFamilies();
};

const makeFamilyOptions = (families: Family[]) => {
  const options = families.map((f: Family) => ({
    label: f.name,
    value: f.ref || "",
    mutuallyExclusive: true,
  }));
  options.unshift({
    label: "All Families",
    value: "",
    mutuallyExclusive: true,
  });
  return options;
};

const makeContentOptions = () => {
  return [
    {
      label: "Documents",
      value: "document",
    },
    {
      label: "Emails",
      value: "email",
    },
    {
      label: "Phone calls",
      value: "phonecall",
    },
    {
      label: "Texts",
      value: "textmessage",
    },
    {
      label: "Tasks",
      value: "task,task_block_document",
    },
    {
      label: "Calendar",
      value: "calendar_event",
    },
    {
      label: "Family facts",
      value: "fact",
    },
    {
      label: "Community",
      value: "forum_post,forum_comment",
    },
    {
      label: "Secure info",
      value: "secret",
    },
  ];
};

type Props = {
  filters: Filter[];
  setFilters: (f: Filter[]) => void;
  updateAdminSearch: (b: boolean) => void;
  updateChronological: (b: boolean) => void;
  chronological: boolean;
  adminSearch: boolean;
};

export default ({
  filters,
  setFilters,
  updateAdminSearch,
  updateChronological,
  chronological,
  adminSearch,
}: Props) => {
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const [admin, setAdmin] = useState(adminSearch);
  const { data, request } = useListFamilies(admin);

  const getFieldValue = (field: string): string | null => {
    return filters.find((f) => f.field === field)?.value || null;
  };
  const selectedEntityTypes = new Set(
    (getFieldValue("entity_type") || "").split(",").filter(Boolean),
  );
  const selectedSources = makeContentOptions()
    .filter((option) =>
      option.value
        .split(",")
        .some((entityType) => selectedEntityTypes.has(entityType)),
    )
    .map((option) => option.value);
  useEffect(() => {
    setAdmin(adminSearch);
  }, [adminSearch]);
  useEffect(() => {
    request();
  }, [admin]);
  const setOrUpdateFilter = (
    field: string,
    value: string | null,
    operator: string,
    presentationValue?: string,
  ) => {
    const existingFilter = filters.find((f) => f.field === field);
    if (existingFilter && value !== null) {
      existingFilter.value = value;
      existingFilter.operator = operator;
      existingFilter.presentationValue = presentationValue;
      setFilters([...filters]);
    } else if (value == null) {
      setFilters(filters.filter((f) => f.field !== field));
    } else {
      filters.push({ field, value, operator, presentationValue });
      setFilters([...filters]);
    }
  };
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      gap={"12px"}
      sx={{
        overflowX: "scroll",
        width: "100%",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <CheckedDropDown
        key={getFieldValue("time_range") || "any-time"}
        radio={true}
        initial={[getFieldValue("time_range") || ""]}
        options={Object.values(TIME_RANGES).map((pt) => ({
          label: pt.label,
          value: pt.value || "",
          mutuallyExclusive: true,
        }))}
        sx={{ width: isMobile ? "50%" : "", minWidth: "120px" }}
        displayValue={
          Object.values(TIME_RANGES).find(
            (t) => t.value === getFieldValue("time_range"),
          )?.label || "Date"
        }
        onChange={(r) =>
          r.length === 1 &&
          setOrUpdateFilter("time_range", r[0].value || null, "GREATER_THAN")
        }
      />
      <CheckedDropDown
        disabled={!data}
        radio={true}
        options={makeFamilyOptions(data?.families || [])}
        sx={{ width: isMobile ? "50%" : "", minWidth: "120px" }}
        maxDropdownHeight={"343px"}
        displayValue={
          (data?.families || []).find((f) => f.ref === getFieldValue("family"))
            ?.name || "Family"
        }
        onChange={(f) =>
          f.length === 1 &&
          setOrUpdateFilter("family", f[0].value || null, "EQUALS")
        }
      />
      <CheckedDropDown
        key={getFieldValue("entity_type") || "all-sources"}
        initial={selectedSources}
        options={makeContentOptions()}
        sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
        maxDropdownHeight={"343px"}
        displayValue={
          filters.find((filter) => filter.field === "entity_type")
            ?.presentationValue || "Source"
        }
        onChange={(sources) =>
          setOrUpdateFilter(
            "entity_type",
            sources.length
              ? sources.flatMap((source) => source.value.split(",")).join(",")
              : null,
            "CONTAINED_IN",
            sources.map((source) => source.label).join(", "),
          )
        }
      />
      <CheckBoxFilter
        key={`chronological-${chronological}`}
        label="By date"
        initialChecked={chronological}
        onChange={updateChronological}
      />
      {!isMobile && currentUser.roles.includes(ROLE_ADMIN) && (
        <CheckBoxFilter
          key={`admin-${adminSearch}`}
          label="Admin mode"
          initialChecked={adminSearch}
          onChange={(b) => {
            updateAdminSearch(b);
            setAdmin(b);
          }}
        />
      )}
    </Box>
  );
};
